import { HWS, MAX_ENTRIES, RESLINK, RLM, SCHEMA_VERSION, STORAGE_KEY } from './constants';
import { isNonEmptyString, isPlainObject, isString } from './guards';
import type { RetargetingCandidate, RetargetingStore, UnknownCandidate } from './retargeting.types';

export type RetargetingCandidateStoreReadResult =
  | { status: 'unavailable'; candidates: [] }
  | { status: 'storage_error'; candidates: [] }
  | { status: 'invalid'; candidates: [] }
  | {
      status: 'success';
      candidates: RetargetingCandidate[];
      totalCandidateCount: number;
      rejectedCandidateCount: number;
    };

export function hasLocalStorage(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return typeof window.localStorage !== 'undefined';
  } catch {
    return false;
  }
}

// Reslink stored form: non-empty string[] where every element is a non-empty string. Legacy entries without marshaCode are silently dropped.
function hasValidReslinkMarshaCode(candidate: UnknownCandidate): boolean {
  const value = (candidate as { marshaCode?: unknown }).marshaCode;
  if (!Array.isArray(value) || value.length === 0) return false;
  return value.every(isNonEmptyString);
}

function isRetargetingCandidate(value: unknown): value is RetargetingCandidate {
  if (!isPlainObject(value)) return false;
  const c = value as UnknownCandidate;
  const validBase =
    c.schemaVersion === SCHEMA_VERSION &&
    (c.useCase === RESLINK || c.useCase === RLM || c.useCase === HWS) &&
    isNonEmptyString(c.timestamp) &&
    isNonEmptyString(c.displayName) &&
    isString(c.imageUrl) &&
    isNonEmptyString(c.targetUrl);
  if (!validBase) return false;
  // Reslink may be imageless (the homepage renders its generic image); other use cases still require one.
  if (c.useCase === RESLINK) return hasValidReslinkMarshaCode(c);
  return isNonEmptyString(c.imageUrl);
}

function readRawStorage(storage: Storage): { status: 'success'; raw: string | null } | { status: 'storage_error' } {
  try {
    return { status: 'success', raw: storage.getItem(STORAGE_KEY) };
  } catch {
    return { status: 'storage_error' };
  }
}

function safeJsonParse(raw: string): { status: 'success'; value: unknown } | { status: 'invalid' } {
  try {
    return { status: 'success', value: JSON.parse(raw) };
  } catch {
    return { status: 'invalid' };
  }
}

export function readRetargetingCandidateStoreResult(): RetargetingCandidateStoreReadResult {
  if (typeof window === 'undefined') return { status: 'unavailable', candidates: [] };

  let storage: Storage;
  try {
    if (typeof window.localStorage === 'undefined') return { status: 'unavailable', candidates: [] };
    storage = window.localStorage;
  } catch {
    return { status: 'storage_error', candidates: [] };
  }

  const stored = readRawStorage(storage);
  if (stored.status === 'storage_error') return { status: 'storage_error', candidates: [] };
  if (!stored.raw) return { status: 'unavailable', candidates: [] };

  const parsed = safeJsonParse(stored.raw);
  if (parsed.status === 'invalid' || !isPlainObject(parsed.value) || !Array.isArray(parsed.value['candidates'])) {
    return { status: 'invalid', candidates: [] };
  }

  const storedCandidates = parsed.value['candidates'];
  const candidates = storedCandidates.filter(isRetargetingCandidate);
  return {
    status: 'success',
    candidates,
    totalCandidateCount: storedCandidates.length,
    rejectedCandidateCount: storedCandidates.length - candidates.length,
  };
}

export function readRetargetingCandidateStore(): RetargetingCandidate[] {
  return readRetargetingCandidateStoreResult().candidates;
}

export function writeRetargetingCandidateStore(candidates: RetargetingCandidate[]): void {
  if (!hasLocalStorage()) {
    throw new Error('localStorage unavailable');
  }
  const store: RetargetingStore = {
    schemaVersion: SCHEMA_VERSION,
    candidates,
    maxEntries: MAX_ENTRIES,
  };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

export function isQuotaExceededError(error: unknown): boolean {
  if (!error) return false;

  if (error instanceof Error) {
    if (error.name === 'QuotaExceededError') return true;
    if (error.name === 'NS_ERROR_DOM_QUOTA_REACHED') return true;
  }

  if (typeof error === 'object' && error !== null) {
    const code = (error as { code?: number }).code;
    if (code === 22 || code === 1014) return true;
  }

  return false;
}
